import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { OverlayTrigger, Tooltip, Dropdown } from 'react-bootstrap';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import manageAccount from '../../../assets/images/manage_accounts.svg';
import { useUpdateAgencyProfessionalEnabledStatusMutation } from '../../../redux/external-agency';
import utils from '../../../utils/utils';
import { showNotification } from '../../../redux/notification';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';

const DatatableActionColumn = ({ caregiver }) => {
	const history = useHistory();
	const dispatch = useDispatch();

	const [showActionModal, setShowActionModal] = useState(false);
	const [selectedProfessional, setSelectedProfessional] = useState(null);

	const selectedProfessionalIsDisabled = selectedProfessional?.enabledForAgency === false;

	let actionsEnabled = true;
	if (caregiver.status === 'deactivated') {
		actionsEnabled = false;
	}

	let manageAccountEnabled = true;
	if (caregiver.status === 'deactivated') {
		manageAccountEnabled = false;
	} else if (caregiver.status === 'pending' && (caregiver.isMultiAgency || caregiver.isMultiRole)) {
		manageAccountEnabled = false;
	} else if (
		caregiver.status === 'disabled' &&
		(caregiver.isMultiAgency || caregiver.isMultiRole) &&
		!caregiver.hasJoined
	) {
		manageAccountEnabled = false;
	}

	let message = !manageAccountEnabled
		? "This professional was created by ShiftMed or another agency and their profile can't be seen until the professional accepts your invite."
		: 'Manage profile';

	if (caregiver.status === 'deactivated') {
		message =
			'This professional is currently unavailable to work shifts through our platform at this time. Contact your MSP contact for more details.';
	}

	const handleOpenProfessionalActionModal = (professional) => {
		setSelectedProfessional(professional);
		setShowActionModal(true);
	};

	const [updateProfessionalEnabledStatus] = useUpdateAgencyProfessionalEnabledStatusMutation();

	const handleProfessionalAction = async () => {
		setShowActionModal(false);

		const payload = { is_enabled: !selectedProfessional.enabledForAgency };

		try {
			await updateProfessionalEnabledStatus({ caregiverId: selectedProfessional.id, body: payload }).unwrap();
		} catch (error) {
			const errorMessage = utils.handleNetworkErrorMessage({ response: error });
			dispatch(showNotification('error', errorMessage));
		}

		setSelectedProfessional(null);
	};

	const actionMenuTemplate = (item) => {
		const isAgencyEnabled = item.enabledForAgency;

		return (
			<div className="action-menu-container agency-professional-actions">
				{!actionsEnabled ? (
					<Dropdown className="shift-action-dropdown">
						<Dropdown.Toggle className="action-menu disabled" />
					</Dropdown>
				) : (
					<Dropdown className="shift-action-dropdown">
						<OverlayTrigger
							trigger={['hover']}
							placement="bottom"
							key="shift-actions-menu-tooltip"
							overlay={<Tooltip className="shift-actions-tooltip">More actions</Tooltip>}
						>
							<Dropdown.Toggle className="action-menu" />
						</OverlayTrigger>
						<Dropdown.Menu>
							<Dropdown.Item
								className={classNames('action-menu-item', {
									disabled: !actionsEnabled
								})}
								onClick={() => {
									handleOpenProfessionalActionModal(item);
								}}
							>
								<div className="action-menu-item-container">
									<div>{isAgencyEnabled ? 'Disable professional' : 'Enable professional'}</div>
								</div>
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				)}
			</div>
		);
	};

	return (
		<div className="actions-container">
			<OverlayTrigger
				placement="bottom"
				overlay={
					<Tooltip style={{ fontSize: '1.6rem' }}>
						<div style={{ padding: '0.5rem' }}>{message}</div>
					</Tooltip>
				}
			>
				<button
					type="button"
					className={classNames('btn', {
						disabled: !manageAccountEnabled
					})}
					onClick={() => history.push(`/private/agency/professionals/${caregiver.id}/employee-details`)}
					aria-label="Manage Profile"
					disabled={!manageAccountEnabled}
				>
					<img src={manageAccount} alt="Manage account" />
				</button>
			</OverlayTrigger>
			{actionMenuTemplate(caregiver)}

			{showActionModal && selectedProfessional && (
				<ConfirmationModal
					key="confirmation-workforce-modal"
					size="l"
					className={classNames('workforce-confirmation-modal')}
					title={selectedProfessionalIsDisabled ? 'Enable professional?' : 'Disable professional?'}
					cancelLabel={selectedProfessionalIsDisabled ? 'Keep disabled' : 'Keep enabled'}
					onCancel={() => setShowActionModal(false)}
					onConfirm={handleProfessionalAction}
					confirmLabel={selectedProfessionalIsDisabled ? 'Enable professional' : 'Disable professional'}
					disableBackdrop
				>
					<div style={{ marginTop: '1rem' }}>
						<p>
							{selectedProfessionalIsDisabled ? (
								'This professional is currently disabled for your agency; if you enable them, they will be able to work shifts on your behalf.'
							) : (
								<>
									This professional is currently enabled for your agency; if you disable them, they will{' '}
									<b>no longer</b> be able to work shifts on your behalf.
								</>
							)}
						</p>
					</div>
				</ConfirmationModal>
			)}
		</div>
	);
};

export default DatatableActionColumn;
